import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  FileDownload as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Analytics as AnalyticsIcon,
  DeleteForever as DeleteForeverIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import AnalisisPUModal from '../components/AnalisisPUModal';
import exportService from '../services/exportService';
import customTemplateService from '../services/customTemplateService';
import customTemplateServiceExcelJS from '../services/customTemplateServiceExcelJS';
import axios from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SettingsIcon from '@mui/icons-material/Settings';
import API_URL from '../config/api';

function EditorPresupuesto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [presupuesto, setPresupuesto] = useState(null);
  const [conceptos, setConceptos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [porcentajes, setPorcentajes] = useState({
    indirectos: 17,
    financiamiento: 2,
    utilidad: 10,
    iva: 16
  });
  const [totales, setTotales] = useState({
    costoDirecto: 0,
    indirectos: 0,
    financiamiento: 0,
    utilidad: 0,
    subtotal: 0,
    iva: 0,
    total: 0
  });

  // Estados para procesamiento con IA
  const [procesandoIA, setProcesandoIA] = useState(false);
  const [progresoIA, setProgresoIA] = useState({
    total: 0,
    procesados: 0,
    actual: '',
    errores: []
  });
  const [confirmarProcesamiento, setConfirmarProcesamiento] = useState(false);
  const [modalAnalisisPU, setModalAnalisisPU] = useState(false);
  const [conceptoSeleccionado, setConceptoSeleccionado] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Estados para menú de exportación con plantillas
  const [excelMenuAnchor, setExcelMenuAnchor] = useState(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null);
  const [selectedExcelTemplate, setSelectedExcelTemplate] = useState('standard');
  const [selectedPdfTemplate, setSelectedPdfTemplate] = useState('standard');
  const [customTemplates, setCustomTemplates] = useState({});

  useEffect(() => {
    fetchPresupuesto();
    // Cargar plantillas personalizadas (ambas versiones)
    const savedV2 = localStorage.getItem('custom_excel_templates_v2');
    const savedV1 = localStorage.getItem('custom_excel_templates');

    let templates = {};

    // Cargar plantillas v1 si existen
    if (savedV1) {
      templates = { ...JSON.parse(savedV1) };
    }

    // Cargar plantillas v2 (ExcelJS) y sobrescribir si tienen el mismo nombre
    if (savedV2) {
      const v2Templates = JSON.parse(savedV2);
      templates = { ...templates, ...v2Templates };
    }

    setCustomTemplates(templates);
  }, [id]);

  const fetchPresupuesto = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/presupuestos/${id}`);
      if (!response.ok) throw new Error('Error al cargar el presupuesto');
      const data = await response.json();
      setPresupuesto(data);

      // Cargar porcentajes del presupuesto guardados
      const porcentajesGuardados = {
        indirectos: data.porcentaje_indirectos || 0,
        financiamiento: data.porcentaje_financiamiento || 0,
        utilidad: data.porcentaje_utilidad || 0,
        iva: 16
      };
      setPorcentajes(porcentajesGuardados);

      // Fetch conceptos
      const conceptosRes = await fetch(`${API_URL}/presupuestos/${id}/conceptos`);
      if (conceptosRes.ok) {
        const conceptosData = await conceptosRes.json();
        // Mapear conceptos de la BD al formato del frontend
        const conceptosMapeados = conceptosData.map(c => ({
          id: c.id,
          codigo: c.codigo,
          descripcion: c.descripcion,
          unidad: c.unidades?.codigo || 'PZA',
          unidad_id: c.unidad_id,
          cantidad: c.cantidad,
          precio_unitario: c.precio_unitario,
          importe: c.cantidad * c.precio_unitario,
          analisis: c.analisis_pu || null
        }));
        setConceptos(conceptosMapeados);
        // Recalcular totales con los porcentajes guardados
        calcularTotales(conceptosMapeados, porcentajesGuardados);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const calcularTotales = (conceptosList, porcentajesParam = null) => {
    const porcents = porcentajesParam || porcentajes;
    const costoDirecto = conceptosList.reduce((sum, c) => sum + (parseFloat(c.importe) || 0), 0);
    const indirectos = costoDirecto * (porcents.indirectos / 100);
    const financiamiento = costoDirecto * (porcents.financiamiento / 100);
    const utilidad = costoDirecto * (porcents.utilidad / 100);
    const subtotal = costoDirecto + indirectos + financiamiento + utilidad;
    const iva = subtotal * (porcents.iva / 100);
    const total = subtotal + iva;

    setTotales({
      costoDirecto,
      indirectos,
      financiamiento,
      utilidad,
      subtotal,
      iva,
      total
    });
  };

  const handleEditRow = (index, concepto) => {
    setEditingRow(index);
    setEditingData({ ...concepto });
  };

  const handleSaveRow = async () => {
    const updatedConceptos = [...conceptos];
    updatedConceptos[editingRow] = editingData;

    // Recalcular importe
    updatedConceptos[editingRow].importe =
      (parseFloat(editingData.cantidad) || 0) * (parseFloat(editingData.precio_unitario) || 0);

    setConceptos(updatedConceptos);
    calcularTotales(updatedConceptos);
    setEditingRow(null);
    setEditingData({});
  };

  const handleCancelEdit = () => {
    setEditingRow(null);
    setEditingData({});
  };

  const handleDeleteRow = (index) => {
    const updatedConceptos = conceptos.filter((_, i) => i !== index);
    setConceptos(updatedConceptos);
    calcularTotales(updatedConceptos);
  };

  const handleAddConcepto = () => {
    const nuevoConcepto = {
      codigo: `C-${conceptos.length + 1}`,
      descripcion: 'Nuevo concepto',
      unidad: 'PZA',
      cantidad: 1,
      precio_unitario: 0,
      importe: 0
    };
    setConceptos([...conceptos, nuevoConcepto]);
  };

  const handleCalcularTodosPU = () => {
    // Filtrar conceptos sin precio
    const conceptosSinPrecio = conceptos.filter(c => !c.precio_unitario || c.precio_unitario === 0);

    if (conceptosSinPrecio.length === 0) {
      toast('Todos los conceptos ya tienen precio unitario asignado', {
        icon: 'ℹ️'
      });
      return;
    }

    setProgresoIA({
      total: conceptosSinPrecio.length,
      procesados: 0,
      actual: '',
      errores: []
    });
    setConfirmarProcesamiento(true);
  };

  const handleSave = async () => {
    try {
      const response = await fetch(`${API_URL}/presupuestos/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...presupuesto,
          conceptos,
          porcentajes,
          totales
        })
      });

      if (!response.ok) {
        throw new Error('Error al guardar');
      }

      toast.success('Presupuesto guardado exitosamente');
    } catch (error) {
      console.error('Error guardando presupuesto:', error);
      toast.error('Error al guardar el presupuesto');
    }
  };

  const procesarConceptosConIA = async () => {
    setConfirmarProcesamiento(false);
    setProcesandoIA(true);

    const conceptosSinPrecio = conceptos.filter(c => !c.precio_unitario || c.precio_unitario === 0);
    const nuevosConceptos = [...conceptos];
    let procesados = 0;
    let errores = [];

    for (const concepto of conceptosSinPrecio) {
      const index = conceptos.findIndex(c => c.codigo === concepto.codigo);

      setProgresoIA(prev => ({
        ...prev,
        actual: concepto.descripcion,
        procesados: procesados
      }));

      try {
        // Llamar a la API para generar el análisis de PU
        const response = await axios.post(`${API_URL}/analisis-pu/generate`, {
          concepto: concepto.descripcion,
          unidad: concepto.unidad,
          ubicacion: presupuesto?.ubicacion || 'Campeche'
        });

        if (response.data) {
          // Calcular el costo directo del análisis
          const totalMateriales = response.data.materiales?.reduce((sum, m) => sum + (m.importe || 0), 0) || 0;
          const totalManoObra = response.data.mano_obra?.reduce((sum, m) => sum + (m.importe || 0), 0) || 0;
          const totalMaquinaria = response.data.maquinaria?.reduce((sum, m) => sum + (m.importe || 0), 0) || 0;
          const totalHerramienta = response.data.herramienta?.reduce((sum, h) => sum + (h.importe || 0), 0) || 0;
          const costoDirecto = totalMateriales + totalManoObra + totalMaquinaria + totalHerramienta;

          // Actualizar el concepto con el nuevo precio
          nuevosConceptos[index] = {
            ...nuevosConceptos[index],
            precio_unitario: costoDirecto,
            importe: costoDirecto * (nuevosConceptos[index].cantidad || 1),
            analisis_pu: response.data
          };

          // Actualizar en tiempo real
          setConceptos([...nuevosConceptos]);
          procesados++;
        }
      } catch (error) {
        console.error(`Error procesando ${concepto.descripcion}:`, error);
        errores.push({
          concepto: concepto.descripcion,
          error: error.message
        });
      }

      // Actualizar progreso
      setProgresoIA(prev => ({
        ...prev,
        procesados: procesados,
        errores: errores
      }));

      // Pequeña pausa entre peticiones para no saturar
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    // Guardar cambios en el backend
    await handleSave();

    setProcesandoIA(false);

    if (errores.length > 0) {
      toast.warning(`Procesados ${procesados} conceptos. ${errores.length} con errores.`);
    } else {
      toast.success(`¡Todos los conceptos procesados exitosamente!`);
    }
  };

  const handlePorcentajeChange = (tipo, valor) => {
    const nuevosP = { ...porcentajes, [tipo]: parseFloat(valor) || 0 };
    setPorcentajes(nuevosP);
    calcularTotales(conceptos, nuevosP);
  };

  const handleOpenAnalisisPU = (concepto) => {
    setConceptoSeleccionado(concepto);
    setModalAnalisisPU(true);
  };

  const handleUpdateConceptoPU = (updatedConcept) => {
    const updatedConceptos = conceptos.map(c =>
      c.codigo === updatedConcept.codigo ? { ...c, precio_unitario: updatedConcept.pu, importe: updatedConcept.importe } : c
    );
    setConceptos(updatedConceptos);
    calcularTotales(updatedConceptos);
  };

  const handleDeletePresupuesto = async () => {
    try {
      const response = await fetch(`${API_URL}/presupuestos/${id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Presupuesto eliminado exitosamente');
        navigate('/');
      }
    } catch (err) {
      console.error('Error eliminando presupuesto:', err);
      alert('Error al eliminar el presupuesto');
    }
    setDeleteConfirmOpen(false);
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    });
  };

  // Nuevas funciones de exportación con plantillas
  const handleExcelExport = (template) => {
    const result = exportService.exportToExcel(presupuesto, conceptos, porcentajes, template);

    if (result.success) {
      toast.success(`Presupuesto exportado a Excel: ${result.fileName}`);
    } else {
      toast.error(`Error al exportar: ${result.error}`);
    }

    setExcelMenuAnchor(null);
  };

  // Manejar exportación con plantilla personalizada
  const handleCustomExcelExport = async (key, template) => {
    try {
      // Verificar si es una plantilla nueva con ExcelJS
      if (template.type === 'exceljs' && template.file?.buffer) {
        // Reconstruir el buffer desde el array guardado
        const buffer = new Uint8Array(template.file.buffer);
        const templateWithBuffer = {
          ...template,
          file: {
            ...template.file,
            buffer: buffer
          }
        };

        const result = await customTemplateServiceExcelJS.applyCustomTemplate(
          presupuesto,
          conceptos,
          porcentajes,
          templateWithBuffer
        );

        if (result.success) {
          toast.success(`Exportado con plantilla personalizada: ${template.name} (con estilos preservados)`);
        } else {
          toast.error(`Error al exportar: ${result.error}`);
        }
      } else {
        // Usar el servicio anterior para plantillas antiguas
        const result = customTemplateService.applyCustomTemplate(
          presupuesto,
          conceptos,
          porcentajes,
          template
        );

        if (result.success) {
          toast.success(`Exportado con plantilla personalizada: ${template.name}`);
        } else {
          toast.error(`Error al exportar: ${result.error}`);
        }
      }
    } catch (error) {
      toast.error('Error al aplicar plantilla personalizada');
      console.error(error);
    }
  };

  const handlePdfExport = (template) => {
    const result = exportService.exportToPDF(presupuesto, conceptos, porcentajes, template);

    if (result.success) {
      toast.success(`Presupuesto exportado a PDF: ${result.fileName}`);
    } else {
      toast.error(`Error al exportar: ${result.error}`);
    }

    setPdfMenuAnchor(null);
  };


  const handleSavePresupuesto = async () => {
    try {
      // Primero guardar los conceptos con los valores correctos y el análisis
      const conceptosToSave = conceptos.map(c => ({
        ...c,
        precio_unitario: c.precio_unitario || c.pu || 0,
        pu: c.precio_unitario || c.pu || 0,
        analisis: c.analisis || null // Incluir el análisis
      }));

      const conceptosResponse = await fetch(`${API_URL}/presupuestos/${id}/conceptos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conceptos: conceptosToSave })
      });

      if (!conceptosResponse.ok) {
        throw new Error('Error guardando conceptos');
      }

      // Luego actualizar el presupuesto con los totales y porcentajes actuales
      const response = await fetch(`${API_URL}/presupuestos/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nombre_obra: presupuesto.nombre_obra,
          cliente: presupuesto.cliente,
          ubicacion: presupuesto.ubicacion,
          tipo_obra: presupuesto.tipo_obra,
          importe_costo_directo: totales.costoDirecto,
          porcentaje_indirectos: porcentajes.indirectos,
          importe_indirectos: totales.indirectos,
          porcentaje_financiamiento: porcentajes.financiamiento,
          importe_financiamiento: totales.financiamiento,
          porcentaje_utilidad: porcentajes.utilidad,
          importe_utilidad: totales.utilidad,
          subtotal: totales.subtotal,
          iva: totales.iva,
          importe_total: totales.total
        })
      });

      if (response.ok) {
        const updatedData = await response.json();
        // Actualizar el estado local con los datos guardados
        setPresupuesto({
          ...presupuesto,
          porcentaje_indirectos: porcentajes.indirectos,
          porcentaje_financiamiento: porcentajes.financiamiento,
          porcentaje_utilidad: porcentajes.utilidad
        });
        alert('Presupuesto guardado exitosamente');
      }
    } catch (err) {
      console.error('Error guardando presupuesto:', err);
      alert('Error al guardar el presupuesto');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
          Volver al Dashboard
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Editor de Presupuesto</Typography>
        <Box>
          <Button startIcon={<SaveIcon />} onClick={handleSavePresupuesto} sx={{ mr: 1 }}>
            Guardar
          </Button>
          <Button
            startIcon={<ExcelIcon />}
            onClick={(e) => setExcelMenuAnchor(e.currentTarget)}
            sx={{ mr: 1 }}
            color="success"
            endIcon={<ArrowBackIcon style={{ transform: 'rotate(270deg)' }} />}
          >
            Excel
          </Button>
          <Button
            startIcon={<PdfIcon />}
            onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
            sx={{ mr: 1 }}
            color="error"
            endIcon={<ArrowBackIcon style={{ transform: 'rotate(270deg)' }} />}
          >
            PDF
          </Button>
          <Button startIcon={<DeleteForeverIcon />} onClick={() => setDeleteConfirmOpen(true)} sx={{ mr: 1 }} color="error">
            Eliminar
          </Button>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
            Volver
          </Button>
        </Box>
      </Box>

      {presupuesto && (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Información General
                  </Typography>
                  <Typography variant="body2">
                    <strong>Obra:</strong> {presupuesto.nombre_obra}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Cliente:</strong> {presupuesto.cliente}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Ubicación:</strong> {presupuesto.ubicacion}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Fecha:</strong> {new Date(presupuesto.fecha_elaboracion).toLocaleDateString('es-MX')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Resumen de Costos
                  </Typography>
                  <Typography variant="body2">
                    <strong>Costo Directo:</strong> {formatCurrency(totales.costoDirecto)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Indirectos:</strong> {formatCurrency(totales.indirectos)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Subtotal:</strong> {formatCurrency(totales.subtotal)}
                  </Typography>
                  <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                    <strong>Total:</strong> {formatCurrency(totales.total)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Conceptos</Typography>
              <Box display="flex" gap={1}>
                <Button
                  startIcon={<CalculateIcon />}
                  onClick={handleCalcularTodosPU}
                  variant="outlined"
                  size="small"
                  disabled={procesandoIA || conceptos.length === 0}
                >
                  Calcular todos los PU con IA
                </Button>
                <Button startIcon={<AddIcon />} onClick={handleAddConcepto} variant="contained" size="small">
                  Agregar Concepto
                </Button>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Unidad</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">P.U.</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {conceptos.map((concepto, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {editingRow === index ? (
                          <TextField
                            value={editingData.codigo}
                            onChange={(e) => setEditingData({ ...editingData, codigo: e.target.value })}
                            size="small"
                            sx={{ width: 100 }}
                          />
                        ) : (
                          concepto.codigo
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRow === index ? (
                          <TextField
                            value={editingData.descripcion}
                            onChange={(e) => setEditingData({ ...editingData, descripcion: e.target.value })}
                            size="small"
                            fullWidth
                          />
                        ) : (
                          concepto.descripcion
                        )}
                      </TableCell>
                      <TableCell>
                        {editingRow === index ? (
                          <TextField
                            value={editingData.unidad}
                            onChange={(e) => setEditingData({ ...editingData, unidad: e.target.value })}
                            size="small"
                            sx={{ width: 80 }}
                          />
                        ) : (
                          concepto.unidad
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {editingRow === index ? (
                          <TextField
                            type="number"
                            value={editingData.cantidad}
                            onChange={(e) => setEditingData({ ...editingData, cantidad: e.target.value })}
                            size="small"
                            sx={{ width: 100 }}
                          />
                        ) : (
                          concepto.cantidad
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center">
                          <Typography>{formatCurrency(concepto.precio_unitario)}</Typography>
                          {editingRow === index && (
                            <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                              (Use Análisis PU)
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">{formatCurrency(concepto.importe)}</TableCell>
                      <TableCell align="center">
                        {editingRow === index ? (
                          <>
                            <IconButton size="small" onClick={handleSaveRow} color="primary">
                              <SaveIcon />
                            </IconButton>
                            <IconButton size="small" onClick={handleCancelEdit}>
                              <ArrowBackIcon />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton size="small" onClick={() => handleOpenAnalisisPU(concepto)} color="primary" title="Análisis PU">
                              <AnalyticsIcon />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleEditRow(index, concepto)} title="Editar">
                              <EditIcon />
                            </IconButton>
                            <IconButton size="small" onClick={() => handleDeleteRow(index)} color="error" title="Eliminar">
                              <DeleteIcon />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {conceptos.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No hay conceptos. Haz clic en "Agregar Concepto" para empezar.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}></Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Costo Directo:</Typography>
                    <Typography>{formatCurrency(totales.costoDirecto)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" alignItems="center">
                      <Typography>Indirectos (</Typography>
                      <TextField
                        type="number"
                        value={porcentajes.indirectos}
                        onChange={(e) => handlePorcentajeChange('indirectos', e.target.value)}
                        size="small"
                        sx={{ width: 60, mx: 0.5 }}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                      />
                      <Typography>%):</Typography>
                    </Box>
                    <Typography>{formatCurrency(totales.indirectos)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" alignItems="center">
                      <Typography>Financiamiento (</Typography>
                      <TextField
                        type="number"
                        value={porcentajes.financiamiento}
                        onChange={(e) => handlePorcentajeChange('financiamiento', e.target.value)}
                        size="small"
                        sx={{ width: 60, mx: 0.5 }}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                      />
                      <Typography>%):</Typography>
                    </Box>
                    <Typography>{formatCurrency(totales.financiamiento)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box display="flex" alignItems="center">
                      <Typography>Utilidad (</Typography>
                      <TextField
                        type="number"
                        value={porcentajes.utilidad}
                        onChange={(e) => handlePorcentajeChange('utilidad', e.target.value)}
                        size="small"
                        sx={{ width: 60, mx: 0.5 }}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                      />
                      <Typography>%):</Typography>
                    </Box>
                    <Typography>{formatCurrency(totales.utilidad)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>Subtotal:</Typography>
                    <Typography>{formatCurrency(totales.subtotal)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography>IVA (16%):</Typography>
                    <Typography>{formatCurrency(totales.iva)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="h6">TOTAL:</Typography>
                    <Typography variant="h6" color="primary">
                      {formatCurrency(totales.total)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </>
      )}

      {/* Dialog de confirmación para eliminar */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar este presupuesto? Esta acción no se puede deshacer.
            <br /><br />
            <strong>Presupuesto:</strong> {presupuesto?.nombre_obra}
            <br />
            <strong>Cliente:</strong> {presupuesto?.cliente}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button onClick={handleDeletePresupuesto} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para procesamiento con IA */}
      <Dialog
        open={confirmarProcesamiento}
        onClose={() => setConfirmarProcesamiento(false)}
      >
        <DialogTitle>Calcular Precios Unitarios con IA</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se van a calcular los precios unitarios para {progresoIA.total} conceptos que no tienen precio asignado.
            <br /><br />
            Este proceso puede tomar varios minutos dependiendo de la cantidad de conceptos.
            <br /><br />
            ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmarProcesamiento(false)}>Cancelar</Button>
          <Button onClick={procesarConceptosConIA} variant="contained" color="primary">
            Iniciar Procesamiento
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de progreso del procesamiento con IA */}
      <Dialog
        open={procesandoIA}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Procesando con Inteligencia Artificial</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              Procesando: {progresoIA.procesados} de {progresoIA.total} conceptos
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(progresoIA.procesados / progresoIA.total) * 100}
              sx={{ height: 10, borderRadius: 5 }}
            />
          </Box>

          {progresoIA.actual && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Analizando:</strong> {progresoIA.actual}
              </Typography>
            </Alert>
          )}

          {progresoIA.errores.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {progresoIA.errores.length} conceptos con errores
              </Typography>
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary">
            Por favor espere mientras se procesan los conceptos...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Modal de Análisis de PU */}
      {modalAnalisisPU && (
        <AnalisisPUModal
          open={modalAnalisisPU}
          onClose={() => {
            setModalAnalisisPU(false);
            setConceptoSeleccionado(null);
          }}
          concept={conceptoSeleccionado}
          porcentajes={porcentajes}
          onSave={(nuevoPrecio, nuevoAnalisis) => {
            const updatedConceptos = conceptos.map((c, idx) => {
              if (editingRow !== null && idx === editingRow) {
                return {
                  ...c,
                  precio_unitario: nuevoPrecio,
                  importe: c.cantidad * nuevoPrecio,
                  analisis: nuevoAnalisis || c.analisis
                };
              } else if (c.codigo === conceptoSeleccionado?.codigo) {
                return {
                  ...c,
                  precio_unitario: nuevoPrecio,
                  importe: c.cantidad * nuevoPrecio,
                  analisis: nuevoAnalisis || c.analisis
                };
              }
              return c;
            });
            setConceptos(updatedConceptos);
            calcularTotales(updatedConceptos);
            setModalAnalisisPU(false);
            setConceptoSeleccionado(null);
          }}
        />
      )}

      {/* Menú de plantillas de Excel */}
      <Menu
        anchorEl={excelMenuAnchor}
        open={Boolean(excelMenuAnchor)}
        onClose={() => setExcelMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleExcelExport('standard')}>
          📊 Estándar
        </MenuItem>
        <MenuItem onClick={() => handleExcelExport('detallada')}>
          📋 Detallada con Análisis
        </MenuItem>
        <MenuItem onClick={() => handleExcelExport('simplificada')}>
          📄 Simplificada
        </MenuItem>
        <MenuItem onClick={() => handleExcelExport('corporativa')}>
          🏢 Corporativa
        </MenuItem>

        {/* Plantillas personalizadas */}
        {Object.keys(customTemplates).length > 0 && (
          <>
            <Divider />
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">PLANTILLAS PERSONALIZADAS</Typography>
            </MenuItem>
            {Object.entries(customTemplates).map(([key, template]) => (
              <MenuItem key={key} onClick={() => {
                handleCustomExcelExport(key, template);
                setExcelMenuAnchor(null);
              }}>
                📄 {template.name}
              </MenuItem>
            ))}
          </>
        )}

        <Divider />
        <MenuItem onClick={() => {
          setExcelMenuAnchor(null);
          navigate('/configuracion-plantillas');
        }}>
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
          Configurar Plantillas
        </MenuItem>
      </Menu>

      {/* Menú de plantillas de PDF */}
      <Menu
        anchorEl={pdfMenuAnchor}
        open={Boolean(pdfMenuAnchor)}
        onClose={() => setPdfMenuAnchor(null)}
      >
        <MenuItem onClick={() => handlePdfExport('standard')}>
          📄 Estándar
        </MenuItem>
        <MenuItem onClick={() => handlePdfExport('detallada')}>
          📊 Detallada
        </MenuItem>
        <MenuItem onClick={() => handlePdfExport('ejecutiva')}>
          💼 Ejecutiva
        </MenuItem>
        <MenuItem onClick={() => handlePdfExport('minimalista')}>
          ⬜ Minimalista
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          setPdfMenuAnchor(null);
          navigate('/configuracion-plantillas');
        }}>
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
          Configurar Plantillas
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default EditorPresupuesto;